import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocale } from '../locale/LocaleContext.js'

// Site-wide Light / Dark / System theming.
// `choice` is the user-facing preference, persisted to localStorage.
// `effective` is the resolved theme that the UI actually applies (light|dark),
// computed from `choice` plus the system prefers-color-scheme in system mode.

export type ThemeChoice = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

interface ThemeContextValue {
  choice: ThemeChoice
  effective: EffectiveTheme
  setChoice: (choice: ThemeChoice) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)
const STORAGE_KEY = 'xr-theme'
const LEGACY_STORAGE_KEY = 'xr.theme'

function readChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'system'
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored

    const legacyStored = window.localStorage?.getItem(LEGACY_STORAGE_KEY)
    if (legacyStored === 'light' || legacyStored === 'dark') return legacyStored
    if (legacyStored === 'auto') return 'system'
  } catch {
    // ignore: storage unavailable
  }
  return 'light'
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [initialChoice] = useState<ThemeChoice>(() => readChoice())
  const [choice, setChoiceState] = useState<ThemeChoice>(initialChoice)
  const [systemDark, setSystemDark] = useState<boolean>(() =>
    initialChoice === 'system' ? systemPrefersDark() : false,
  )

  useEffect(() => {
    if (choice !== 'system') return
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    setSystemDark(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [choice])

  const effective: EffectiveTheme = useMemo(() => {
    if (choice === 'system') return systemDark ? 'dark' : 'light'
    return choice
  }, [choice, systemDark])

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute('data-theme', effective)
    document.documentElement.style.colorScheme = effective
  }, [effective])

  const setChoice = useCallback((next: ThemeChoice) => {
    if (next === 'system') setSystemDark(systemPrefersDark())
    setChoiceState(next)
    if (typeof window === 'undefined') return
    try {
      window.localStorage?.setItem(STORAGE_KEY, next)
    } catch {
      // ignore: best-effort persistence
    }
  }, [])

  const value = useMemo(() => ({ choice, effective, setChoice }), [choice, effective, setChoice])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}

export function ThemeToggle() {
  const { choice, effective, setChoice } = useTheme()
  const { t } = useLocale()
  const options: { key: ThemeChoice; icon: ReactNode; title: string }[] = [
    { key: 'light', icon: <SunIcon />, title: t('themeLight') },
    { key: 'dark', icon: <MoonIcon />, title: t('themeDark') },
    {
      key: 'system',
      icon: <LaptopIcon />,
      title: t('themeAuto'),
    },
  ]
  // On < sm collapse to a single icon button that cycles light -> dark -> system
  // to keep the top bar from wrapping on phones. Segmented control returns at
  // sm and above where horizontal space is plentiful.
  const cycle = () => {
    const order: ThemeChoice[] = ['light', 'dark', 'system']
    const next = order[(order.indexOf(choice) + 1) % order.length]!
    setChoice(next)
  }
  const currentIcon =
    choice === 'system' ? <LaptopIcon /> : effective === 'dark' ? <MoonIcon /> : <SunIcon />
  const currentTitle = options.find((o) => o.key === choice)?.title ?? t('aria.theme')
  return (
    <>
      <button
        type="button"
        title={currentTitle}
        aria-label={currentTitle}
        onClick={cycle}
        className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] sm:hidden"
      >
        {currentIcon}
      </button>
      <div
        className="hidden flex-none overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] sm:inline-flex"
        role="group"
        aria-label={t('aria.theme')}
      >
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            title={o.title}
            aria-label={o.title}
            aria-pressed={choice === o.key}
            onClick={() => setChoice(o.key)}
            className={
              choice === o.key
                ? 'inline-flex h-[26px] items-center justify-center bg-[var(--xr-brand-primary)] px-2.5 text-[var(--xr-brand-primary-ink)]'
                : 'inline-flex h-[26px] items-center justify-center bg-[var(--xr-bg-surface)] px-2.5 text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]'
            }
          >
            {o.icon}
          </button>
        ))}
      </div>
    </>
  )
}

function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
    </svg>
  )
}

function LaptopIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="4" y="5" width="16" height="11" rx="1.5" />
      <path d="M2 19h20M8 19v-1M16 19v-1" />
    </svg>
  )
}
